import { Component, OnInit, AfterViewInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, ChangeDetectorRef, TemplateRef, EmbeddedViewRef } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortalDirective } from '@angular/cdk/portal';
import * as moment from 'moment';

import { FieldComponent, UndoAction } from './field.component';
import { FormField, Link, Disclaimer } from './form-field.model';
import { DynamicOverlaysDirective } from './dynamic-overlays.directive';

@Component({
  selector: 'app-date-field',
  templateUrl: './date-field.component.html',
  styleUrls: ['./date-field.component.scss']
})
export class DateFieldComponent extends FieldComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() draftData: any;
  @Input() dynamicOverlays: DynamicOverlaysDirective;
  @Input() field: FormField;
  @Input() locked: boolean = false;
  @Input() lockValues: boolean = false;
  @Input() templateData: any;
  @Input() workflowId: number;
  @Output() focusChanged = new EventEmitter<boolean>();
  @Output() highlightChanged = new EventEmitter<{highlighted: boolean, color?: string}>();
  @Output() resized = new EventEmitter<FieldComponent>();
  @Output() returnKey = new EventEmitter<FieldComponent>();
  @Output() undoableAction = new EventEmitter<UndoAction>();
  @Output() updateLinked = new EventEmitter<{link: Link, value?: any, categories?: any[], enabled?: boolean}>();
  @Output() viewLoaded = new EventEmitter<FieldComponent>();
  @ViewChild("versionTemplate") versionTemplate: TemplateRef<null>;
  @ViewChild("versionElement") versionElement: ElementRef;

  defaultValue: moment.Moment;
  disclaimer: Disclaimer;
  format: string = "M/D/YYYY";
  focused: boolean = false;
  id: string;
  initialized: boolean = false;
  highlightColor: string;
  highlightPattern: string;
  highlightValues: string[];
  highlighted: boolean = false;
  label: string;
  max: moment.Moment;
  min: moment.Moment;
  placeholder: string;
  previousValue: moment.Moment
  required: boolean = false;
  showingPreviousValue: boolean = false;
  value: moment.Moment;
  versionChanged: boolean = false;
  versionView: EmbeddedViewRef<null>;
  get blank() { return this.value == null }
  get touched(): boolean {return this.control != null && this.control.touched}
  get valid(): boolean {return this.locked == true || (this.control != null && this.control.valid)}

  constructor(
    cd: ChangeDetectorRef,
    public overlay: Overlay) { super(cd) }

  ngOnInit() {
    this.id = this.field.id;
    this.label = this.field.label;
    this.placeholder = this.field.placeholder;
    this.required = this.field.required;
    this.highlightColor = this.field.highlightColor;
    this.highlightPattern = this.field.highlightPattern;
    this.highlightValues = this.field.highlightValues;
    if (this.field.locked) { this.locked = true }
    if (this.field.format != null) { this.format = this.field.format };

    if (this.field.min != null) {
      this.min = moment().add(this.field.min, 'days');
    } else {
      this.min = moment().add(-100, 'years');
    }
    if (this.field.max != null) {
      this.max = moment().add(this.field.max, 'days');
    } else {
      this.max = moment().add(100, 'years');
    }

    this.loadDefaultValue();
    this.loadInitialState(this.draftData);
  }

  ngAfterViewInit() {
    this.viewLoaded.emit(this);
    setTimeout(() => this.onDateChange(), 100);
  }

  ngOnDestroy() {
    this.hidePreviousValue();
  }

  loadDefaultValue() {
    if (this.field.defaultValue != null) {
      let value = moment(this.field.defaultValue.stored, "YYYY-MM-DD");
      if (value.isValid) {
        this.defaultValue = value;
      }
    } else if (this.id === "expiration_date") {
      let today = moment();
      this.defaultValue = today.add(10, 'days');
    }
  }

  loadValue(draftValue?) {
    let value = draftValue != null ? draftValue : this.templateData;
    if (value instanceof Array) {
      value = value[0].stored
    }
    let valueMoment = moment(value, "YYYY-MM-DD");
    if (valueMoment.isValid()) {
      this.value = valueMoment;
    }
    if (this.value == null) {
      this.value = this.defaultValue;
    }
  }

  loadInitialState(draftValue?) {
    let validators = [];
    if (this.required === true) {validators.push(Validators.required)}
    this.loadValue(draftValue);
    this.control = new FormControl({value: this.value, disabled: this.locked}, validators);
    this.control.valueChanges.subscribe((value) => {
      this._dirty = this.initialized && !this.valuesEqual(value, this.value);
      this.value = value;
      this.initialized = true;
      this.emitValue();
    })
    // Emit initial validity and value
    this.control.updateValueAndValidity();
  }

  descriptionForError(key, value): string {
    switch (key) {
    case 'matDatepickerMax':
      return 'must be before ' + value.max.format('ll');
    case 'matDatepickerMin':
      return 'must be after ' + value.min.format('ll');
    default:
      return super.descriptionForError(key, value);
    }
  }

  emitValue() {
    let value: string = null;
    if (this.value != null) {
      let valueMoment = moment(this.value);
      value = valueMoment.isValid() ? valueMoment.format('YYYY-MM-DD') : null;
    }
    this.valueChanged.emit({field: this.id, value: value});
    this.checkHighlight();
  }

  setValue(value: any, emit: boolean = true) {
    this.value = value;
    this.control.setValue(value, {emitEvent: false});
    if (emit) { this.emitValue(); }
  }

  setPreviousValue(value) {
    if (value != null) {
      let valueMoment = moment(value, "YYYY-MM-DD");
      this.previousValue = valueMoment.isValid() ? valueMoment : null;
    } else {
      this.previousValue = null
    }
    if (this.value == null || this.previousValue == null) {
      this.versionChanged = this.value !== this.previousValue;
    } else {
      this.versionChanged = !this.value.isSame(this.previousValue, 'day');
    }
    if (this.versionChanged) {
      this.showPreviousValue();
    }
    this.cd.markForCheck();
  }

  resetPreviousValue() {
    this.previousValue = null;
    this.versionChanged = false;
    this.hidePreviousValue();
    this.cd.markForCheck();
  }

  onFocus(event) {
    this.focused = true;
    this.focusChanged.emit(true);
    return true;
  }

  onBlur(event) {
    this.onDateChange();
    this.focused = false;
    this.focusChanged.emit(false);
    return true;
  }

  onDateChange() {
    if (this.value != null) {
      let valueMoment = moment(this.value);
      if (valueMoment.isValid()) {
        let formattedValue = valueMoment.format(this.format);
        this.input.nativeElement.value = formattedValue;
      }
    }
  }

  reset() {
    this.loadInitialState();
    this.cd.markForCheck();
  }

  compare(other): number {
    let a = moment(this.value);
    let b = moment(other.value);
    if (!a.isValid()) {
      return 1;
    } else if (!b.isValid()) {
      return -1;
    } else {
      return a > b ? 1 : (a < b ? -1 : 0);
    }
  }

  updateLinkedFields() { }
  
  setCategories(category) { }

  checkHighlight() {
    let value = this.value != null ? moment(this.value).format('YYYY-MM-DD') : '';
    if (this.highlightValues != null) {
      let match = false;
      for (let v of this.highlightValues) {
        if (v === value) {
          match = true;
          break;
        }
      }
      this.highlighted = match;
      this.highlightChanged.emit({highlighted: this.highlighted});
    } else if (this.highlightPattern != null) {
      let regex = new RegExp(this.highlightPattern);
      this.highlighted = regex.test(value);
      this.highlightChanged.emit({highlighted: this.highlighted});
    }
  }

  showPreviousValue() {
    this.showingPreviousValue = true;
    let container = this.dynamicOverlays.viewContainerRef;
    this.versionView = container.createEmbeddedView(this.versionTemplate);
    this.positionOverlays();
  }

  hidePreviousValue() {
    if (this.versionView != null) {
      this.versionView.destroy();
    }
    this.showingPreviousValue = false;
  }

  togglePreviousValue() {
    if (this.showingPreviousValue) {
      this.hidePreviousValue();
    } else {
      this.showPreviousValue();
    }
  }

  positionOverlays() {
    if (this.versionView != null) {
      let rect = this.versionElement.nativeElement.getBoundingClientRect();
      let rootNode = this.versionView.rootNodes.find((n) => n.tagName == 'DIV');
      rootNode.style.left = '' + (rect.left + window.pageXOffset) + 'px';
      rootNode.style.top = '' + (rect.top + window.pageYOffset) + 'px';
      rootNode.style.maxWidth = '' + rect.width + 'px';
    }
  }

  setPatternValue(field: string, value: any) { }

  undo(action: UndoAction) { }

  setLockValues(state) { }

  valuesEqual(a,b): boolean {
    if (a == null || b == null) {
      return a == b;
    } else {
      return a.isSame(b, 'day');
    }
  }

  valueFromTemplate(templateValue) {
    return templateValue != null ? moment(templateValue, 'YYYY-MM-DD') : null;
  }
}


// WEBPACK FOOTER //
// ./src/app/forms/date-field.component.ts